import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import RecurringMeetingItem from 'Shared/components/RecurringMeetingItem/RecurringMeetingItem';
import { WorkspaceType } from 'Shared/constants/types';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';

import { generateFormattedRecurringMeeting } from './utils';
import SyncMeetingsModalBodyWrapper from '../SyncMeetingsModalBodyWrapper/SyncMeetingsModalBodyWrapper';

type Props = {
  countWorkspaces: number;
  startSubmit: boolean;
  setIsSubmitting: Function;
  setDisabledSubmit: Function;
  onUpdateStartSubmit: Function;
  onToggle: Function;
};

function SyncMeetingsModalBodyFinalStep({
  setIsSubmitting,
  onUpdateStartSubmit,
  countWorkspaces,
  setDisabledSubmit,
  startSubmit,
  onToggle,
}: Props) {
  const dispatch = useDispatch();
  const currentUserEmail = useSelector(state => state.user.email);
  const recurringMeetings = useSelector(state => state.user.recurringMeetings);
  const activeRecurringMeetings = recurringMeetings.filter(
    ({ cancelled, hasWorkspace }) => !cancelled && !hasWorkspace
  );
  const [formattedRecurringMeetings, setFormattedRecurringMeetings] = useState(
    activeRecurringMeetings
      .map(({ id, name, attendees, source }) =>
        generateFormattedRecurringMeeting({ recurringMeetingId: id, name, attendees, source })
      )
      .filter(meeting => meeting)
  );
  const [isShowAll, setIsShowAll] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const filteredRecurringMeetings = searchValue
    ? formattedRecurringMeetings.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()))
    : formattedRecurringMeetings;
  const visibleMeetings = isShowAll ? filteredRecurringMeetings : filteredRecurringMeetings.slice(0, 2);
  const showViewMoreButton = visibleMeetings.length < filteredRecurringMeetings.length;

  useEffect(() => {
    if (isShowAll) setIsShowAll(false);
  }, [searchValue]);

  const handleSearchValueChange = useCallback(event => setSearchValue(event.target.value), []);

  const handleViewMoreClick = useCallback(() => setIsShowAll(true), []);

  const handleFormattedRecurringMeetingUpdate = useCallback(
    (uuid, params) =>
      setFormattedRecurringMeetings(
        formattedRecurringMeetings.map(meeting => {
          if (meeting.uuid === uuid)
            return {
              ...meeting,
              ...params,
            };
          return meeting;
        })
      ),
    [formattedRecurringMeetings]
  );

  const handleSubmit = useCallback(async () => {
    onUpdateStartSubmit(false);
    const selectedRecurringMeetings = formattedRecurringMeetings.filter(({ selected }) => selected);
    if (!selectedRecurringMeetings.length) return onToggle();
    setIsSubmitting(true);
    setDisabledSubmit(true);
    const workspacesParams = selectedRecurringMeetings.map(
      ({ name, attendees, inviteAttendees, personalMessage, recurringMeetingId }) => {
        const isCreateTeammate = attendees.length === 2 && attendees.find(({ email }) => currentUserEmail === email);
        const teammate = attendees.find(({ email }) => currentUserEmail !== email);
        return {
          name: isCreateTeammate ? teammate?.fullName || teammate?.email : name,
          isGroup: !isCreateTeammate,
          recurringMeetingId,
          inviteeEmails: inviteAttendees ? attendees.map(({ email }) => email) : null,
          personalMessage,
        };
      }
    );

    await dispatch(bulkCreateWorkspaces({ workspacesParams, showDefaultSuccessMessage: false }));
    onUpdateStartSubmit(false);
    setIsSubmitting(false);
    WpToastManager.success('You have successfully connected your calendar');
    return onToggle();
  }, [formattedRecurringMeetings]);

  useEffect(() => {
    if (startSubmit) handleSubmit();
  }, [startSubmit]);

  return (
    <SyncMeetingsModalBodyWrapper
      description={
        <>
          <div style={{ fontWeight: 600 }}>Would you like to create any workspaces from a recurring meeting?</div>{' '}
          Select meetings to create a workspace or click ‘finish’ to skip and finish calendar sync.
          <span>{` (${countWorkspaces} of ${countWorkspaces})`}</span>
        </>
      }
      searchValue={searchValue}
      onSearchValueChange={handleSearchValueChange}
      showViewMoreButton={showViewMoreButton}
      recurringMeetingsLabel="Other Recurring Meetings"
      viewMoreButtonText="View more meetings"
      onViewMoreClick={handleViewMoreClick}
    >
      {visibleMeetings.length ? (
        visibleMeetings.map(meeting => (
          <RecurringMeetingItem
            key={meeting.uuid}
            type={WorkspaceType.isGroup}
            paramsForCreateWorkspace={meeting}
            currentUserEmail={currentUserEmail}
            onParamsForCreateWorkspaceUpdate={handleFormattedRecurringMeetingUpdate}
          />
        ))
      ) : (
        <div className="text-center mt-4">No Recurring Meetings</div>
      )}
    </SyncMeetingsModalBodyWrapper>
  );
}

export default SyncMeetingsModalBodyFinalStep;
